import { goBack } from "assets/icons";
import styled from "@emotion/styled";
import { Card, Col, Form, Row, Typography, UploadFile, message } from "antd";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "store";
import { FC, useEffect, useState } from "react";
import dayjs from "dayjs";
import SettingFactoryForm from "./SettingFactory";
// uuid
import { v4 as uuidv4, validate } from "uuid";
import { useUpdateFactory, useUserUpdateProfile } from "hooks/useAuth";
import { getAuthProfile } from "services/authAPI";
import { setProfileAndContact } from "store/slices/authSlice";
import { useAppDispatch } from "store";

type Location = {
  lat: number;
  lng: number;
}

interface SettingFactoryProps {
  /** true = แสดงเฉพาะฟอร์ม (ไม่มีการ์ด/หัวเรื่อง/ปุ่มย้อนกลับ) — ใช้ตอนยัดลงจอบังคับตั้งค่า */
  embedded?: boolean;
}

const SettingFactory: FC<SettingFactoryProps> = ({ embedded = false }) => {
  const [avatar, setAvatar] = useState<UploadFile[] | undefined>([]);

  const [location, setLocation] = useState<Location>({
    lat: 13.736717,
    lng: 100.523186,
  });

  const navigate = useNavigate();
  const { contact, profile } = useAppSelector((state) => state.auth);

  const { mutate, isPending } = useUpdateFactory();
  /* อีเมลไม่ได้อยู่บนตาราง factory — เก็บที่บัญชีเจ้าของร้าน (user.email) คนละเส้นกัน */
  const { mutateAsync: updateOwner } = useUserUpdateProfile();

  const [form] = Form.useForm();

    const dispatch = useAppDispatch();

  const onFinish = (values: any) => {
    /*
     * id ของร้าน — `contact` เป็น null ได้ (ร้านที่อนุมัติมาแบบยังไม่มีที่อยู่ จะยังไม่มี
     * แถวใน vw_factory จนกว่าจะบันทึกที่อยู่ครั้งแรก) จึงตกไปใช้ ref_id ของ role แทน
     * ซึ่งคือ factory_id ตัวเดียวกันและมีเสมอเมื่อล็อกอินเป็นร้าน
     */
    const factoryId = contact?.id || profile?.user.role.ref_id || "";

    /*
     * อีเมล = ของบัญชีเจ้าของร้าน (user.email) ไม่ใช่คอลัมน์ของ factory จึงเป็นคนละเส้น
     * ยิงเฉพาะตอนค่าเปลี่ยนจริง และ **ยิงให้เสร็จก่อนดึงโปรไฟล์ใหม่** ไม่งั้นโปรไฟล์ที่ได้
     * กลับมาจะยังเป็นอีเมลเดิม แล้ว effect ข้างล่างเอาค่าเก่ามาทับสิ่งที่เขาเพิ่งกรอก
     * (backend ฝั่ง update_contact_factory ไม่แตะช่องที่ไม่ได้ส่งมาแล้ว ชื่อ/เบอร์/รูป
     *  ของเจ้าของร้านจึงไม่ถูกล้างทิ้ง)
     */
    const nextEmail = (values.email ?? "").trim();
    const emailChanged =
      !!profile?.user?.id && !!nextEmail && nextEmail !== (profile.user.email ?? "").trim();

    mutate(
      {
        id: factoryId,
        name: values.name,
        // หมวดหมู่ส่งเฉพาะตอนที่เลือกมาเป็น uuid จริง (ของเดิมแยกเป็นสองก้อนโค้ดที่เหมือนกันทุกบรรทัด)
        ...(validate(values.category) ? { category_id: values.category } : {}),
        // ร้านใหม่ยังไม่มีรูป — ไม่ได้อัปโหลดก็ส่งค่าว่าง ไม่ใช่ปล่อยให้ [0].url พัง
        image: values.image?.[0]?.url ?? "",
        address: {
          line_1: values.line_1,
          line_2: "",
          sub_district_id: values.sub_district_id,
          district_id: values.district_id,
          province_id: values.province_id,
          post_code: values.zip_code,
          latitude: location.lat.toString(),
          longitude: location.lng.toString(),
        },
      },
      {
        onSuccess: async () => {
          if (emailChanged) {
            try {
              await updateOwner({ id: profile!.user.id, email: nextEmail });
            } catch {
              message.warning("บันทึกข้อมูลกิจการแล้ว แต่เปลี่ยนอีเมลไม่สำเร็จ");
            }
          }
          message.success("บันทึกข้อมูลผู้ประกอบการสำเร็จ");
          getAuthProfile().then((res) => {
            if (res.status === "success") {
              // contact ที่ได้กลับมาทำให้จอบังคับตั้งค่า (FactorySetupGate) ปิดตัวเอง
              dispatch(setProfileAndContact({ profile: res.profile, contact: res.contact }));
            }
          });
        },
        onError: () => {
          message.error("บันทึกข้อมูลผู้ประกอบการไม่สำเร็จ");
        },
      }
    );
  };

  useEffect(() => {
    if (contact?.image_url) {
      setAvatar([
        {
          uid: "uuid",
          name: "โลโก้กิจการ",
          url: contact?.image_url,
        },
      ]);
    } else {
      setAvatar(undefined);
    }
    setLocation({
      lat: contact?.gps.latitude ?? 13.736717,
      lng: contact?.gps.longitude ?? 100.523186,
    });
    form.setFieldsValue({
      name: contact?.name,
      category: contact?.category,
      address: contact?.address,
      email: profile?.user?.email ?? "",
      line_1: contact?.address_fac?.line_1 ?? "",
      zip_code: contact?.address_fac?.zip_code ?? "",
      sub_district_id: contact?.address_fac?.sub_district_id ?? "",
      district_id: contact?.address_fac?.district_id ?? "",
      province_id: contact?.address_fac?.province_id ?? "",
      /*
       * ยังไม่มีโลโก้ = กล่องว่าง ไม่ใช่รายการที่ url เป็น undefined
       * ของเดิมใส่ไว้หนึ่งรายการเสมอ ร้านใหม่จึงเห็นรูปแตกค้างอยู่ ต้องกดถังขยะลบเองก่อน
       * ถึงจะอัปโหลดของจริงได้
       */
      image: contact?.image_url
        ? [{ uid: "-1", name: "โลโก้กิจการ", status: "done", id: "", url: contact.image_url }]
        : [],
    });
  }, [contact, profile, form]);

  const formNode = (
    <SettingFactoryForm
      form={form}
      onFinish={onFinish}
      avatar={avatar}
      address_fac={contact?.address_fac}
      location={location}
      setLocation={setLocation}
    />
  );

  if (embedded) return formNode;

  return (
    <>
      <Row className="rowgap-vbox" gutter={[24, 0]}>
        <Col span={24}>
          <Card bordered={false} className="criclebox h-full tabs-mobile">
            <HeaderTabs>
              <IconWrapper
                onClick={() => {
                  navigate("/setting");
                }}
              >
                <SpanIcon>{goBack}</SpanIcon>
              </IconWrapper>
              <Typography.Title level={5}>ตั้งค่ากิจการ</Typography.Title>
              <RightIconWrapper></RightIconWrapper>
            </HeaderTabs>
            <WrapperAll>{formNode}</WrapperAll>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default SettingFactory;

const HeaderTabs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  flex-direction: row;
  border-bottom: 1px solid #efefef;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 30px;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: #efefef;
  }
`;

const SpanIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const RightIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const WrapperAll = styled.div`
  display: block;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  background-color: #efefef;
`;
